import { Select3, type Select3Props } from "@el8/vital";
import type { HTMLAttributes } from "react";

import { useAudioInputDeviceSelect } from "../hooks/useAudioInputDeviceSelect";

type Props = Omit<
  Select3Props<string, MediaDeviceInfo>,
  "items" | "onChange" | "value"
> & {
  onChange?: (value: string) => void;
};

/**
 * Select component to pick an audio input device for scribe. On mount, requests
 * microphone permission, creates the initial MediaStream (stored in scribe store),
 * and populates the device list. The persisted microphone choice is restored when present.
 */
export function AudioInputDeviceSelect({
  disabled,
  onChange,
  rootProps,
  ...props
}: Props): JSX.Element {
  const { devices, selectedDeviceId, selectDevice } =
    useAudioInputDeviceSelect();

  const handleChange = (deviceId: string): void => {
    selectDevice(deviceId);
    onChange?.(deviceId);
  };

  return (
    <Select3<string, MediaDeviceInfo>
      disabled={disabled}
      getItemKey={(item) => item.deviceId}
      getItemLabel={(item) => item.label}
      getItemValue={(item) => item.deviceId}
      inlineDropdown
      items={devices}
      multiline
      name="audioInputDevice"
      onChange={handleChange}
      placeholder="----"
      value={selectedDeviceId ?? undefined}
      rootProps={
        {
          "data-dd-privacy": "allow", // Avoid masking the options in the Microphone Select
          ...rootProps,
        } as unknown as HTMLAttributes<HTMLDivElement>
      }
      {...props}
    />
  );
}
